/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import SystemClient from "../../SystemInfo/SystemClient";
import { login } from "../../Utils/auth";
import IAuthenticationContext from "./IAuthenticationContext";

export interface AuthenticationState {
    allowAnonymous: boolean;
    isInitialized: boolean;
    isLoggedIn: boolean;
}

export default class AuthenticationContextInstance implements IAuthenticationContext {
    private readonly _state: AuthenticationState;
    private readonly _setState: React.Dispatch<React.SetStateAction<AuthenticationState>>;
    private readonly _systemClient = new SystemClient();

    public constructor() {
        [this._state, this._setState] = React.useState<AuthenticationState>({
            isInitialized: false,
            isLoggedIn: false,
            allowAnonymous: false,
        });

        this.useCheckLoggedIn();
        this.useLoginIfRequired();
    }

    public get isLoggedIn(): boolean {
        return this._state.isLoggedIn;
    }

    public get isLoginDialogOpen(): boolean {
        return !this.isLoggedIn && !this.allowAnonymous && this.isInitialized;
    }

    public get isInitialized(): boolean {
        return this._state.isInitialized;
    }

    public get requiresLogin(): boolean {
        return !this._state.isLoggedIn && !this._state.allowAnonymous;
    }

    public get allowAnonymous(): boolean {
        return this._state.allowAnonymous;
    }

    public setIsLoggedIn() {
        this._setState(prev => ({
            ...prev,
            isLoggedIn: true,
        }));
    }

    public setIsLoggedOut() {
        this._setState(prev => ({
            ...prev,
            isLoggedIn: false,
        }));
    }

    public setAllowAnonymous(value: boolean): void {
        this._setState(prev => ({
            ...prev,
            isInitialized: true,
            allowAnonymous: value,
        }));
    }

    private useCheckLoggedIn = () => {
        React.useEffect(() => {
            this._systemClient
                .istAngemeldet()
                .then(istAngemeldet => {
                    this._setState(prev => ({
                        ...prev,
                        isLoggedIn: istAngemeldet,
                    }));
                })
                .catch(() => {
                    this._setState(prev => ({
                        ...prev,
                        isLoggedIn: false,
                    }));
                });
        }, [])
    }

    private useLoginIfRequired = () => {
        const { isInitialized, requiresLogin } = this;
        React.useEffect(() => {
            if (isInitialized && requiresLogin) {
                login();
            }
        }, [isInitialized, requiresLogin, login])
    }
}
